import { _ } from "../polyglot/polyglot.js";

/**
 * Check if the value is null or undefined.
 *
 * @param {any} value - The value to check
 * @return {boolean}
 */
export function isNull(value) {
	return value === undefined || value === null;
}

/**
 * Check if the value is not null nor undefined.
 *
 * @param {any} value - The value to check
 * @return {boolean}
 */
export function isNotNull(value) {
	return !isNull(value);
}

/**
 * Check if the provided array is null or empty.
 *
 * @param {Array} array - The array to check
 * @return {boolean}
 */
export function isNullOrEmpty(array) {
	return isNull(array) || array.length === 0;
}

/**
 * Return the default value when the passed value is null or undefined.
 * Otherwise, return the value.
 *
 * @param {any} value - Value to test
 * @param {any} defaultValue - Default value to use when the test is positive
 * @return {any}
 */
export function valueOrDefault(value, defaultValue) {
	return isNull(value) ? defaultValue : value;
}

/**
 * Return the default value when the passed value is null, undefined, or an empty string.
 * We can't use `defaultValue=_("N/A").toString()` as this will be resolved at load time,
 * thus always resolving to the default language.
 *
 * @param {string} value - String to test
 * @param {string} [defaultValue] - default string to use when the test is positive
 * @return {string}
 */
export function nonEmptyValueOrDefault({ value, defaultValue }) {
	const baseValue = _("N/A").toString();
	if (value === "") {
		return defaultValue ?? baseValue;
	}

	return value ?? defaultValue ?? baseValue;
}

/**
 * Return the value nested in the object at the provided path.
 *
 * @param {object} obj - Object to get the value from
 * @param {string} path - Path to the value, separated by dots
 * @return {any}
 */
export function getPathValue(obj, path) {
	if (typeof path === "string") {
		path = path.split(".");
	}

	let value = obj;

	path.forEach((attr) => {
		if (value !== null && hasOwnProp(value, attr)) {
			value = value[attr];
		} else {
			value = null;
		}
	});

	return value;
}

/**
 * Set the value at the provided path in the object.
 *
 * @param {object} obj - Object to set the value in
 * @param {string} path - Path to the value, separated by dots
 * @param {any} val - Value to set
 */
export function setPathValue(obj, path, val) {
	if (typeof path === "string") {
		path = path.split(".");
	}

	let node = obj;
	for (let i = 0; i < path.length; i++) {
		const attr = path[i];
		if (i < path.length - 1) {
			if (node[attr] === undefined) {
				node[attr] = {};
			}
		} else {
			node[attr] = val;
		}
		node = node[attr];
	}
}

/**
 * Check if the provided object is a literal object.
 *
 * @param {any} object - The object to check
 * @return {boolean}
 */
export function isObjectLiteral(object) {
	return typeof object === "object" && object?.constructor === Object;
}

// Accessing an object's `hasOwnProperty()` method is not recommended:
// https://eslint.org/docs/rules/no-prototype-builtins
/**
 * Check if the object has the provided property as its own.
 *
 * @param {object} object - The object to check
 * @param {string} propertyName - The property name to check
 * @return {boolean}
 */
export function hasOwnProp(object, propertyName) {
	return Object.prototype.hasOwnProperty.call(object, propertyName);
}

/**
 * Return the argument after removing all key/value pairs for which the
 * value is undefined. The argument is modified in place.
 */
export function removeUndefinedValues(object) {
	Object.keys(object).forEach((key) => {
		if (object[key] !== undefined) {
			return;
		}
		delete object[key];
	});

	return object;
}

/**
 * Apply `transformValueFn` to all the values of `object` and store it in the according key.
 * It behaves similarly as `Array.prototype.map`.
 *
 * @param {object} object - Any object
 * @param {function} transformValueFn - Function to apply on each value of the object
 * @param {function} [transformKeyFn] - Function to apply on each key of the object
 * @return {object} New object with transformed values
 */
export function map(object, transformValueFn, transformKeyFn = (each) => each) {
	return Object.keys(object).reduce(
		(accumulator, key) => ({
			...accumulator,
			[transformKeyFn(key)]: transformValueFn(object[key], key, object),
		}),
		{},
	);
}

/**
 * Add properties of `source` into `target`, overriding if `target`
 * already has a property of the same name. This function doesn't
 * return anything to make the "side-effect" intent clear.
 *
 * @param {object} target - This object will be modified with
 *   properties coming from `source`
 * @param {object} source - This object's properties will be copied to
 *   `target`
 */
export function extend(target, source) {
	for (const key of Object.keys(source)) {
		if (source[key] === undefined) continue;

		target[key] = source[key];
	}
}

/**
 * Deep merge all the literal objects passed as arguments.
 * The arguments aren't mutated.
 *
 * @example
 *
 * deepMerge({a: {b: 1}}, {a: {c: 2}}); // {a: {b:1, c:2}}
 * @param {...object} objects - List of literal objects to merge
 * @return {object}
 */
export function deepMerge(...objects) {
	return objects.reduce(
		(result, current) => deepMergeTwoObjects(result, current),
		{},
	);
}

function deepMergeTwoObjects(firstObject, secondObject) {
	if (!isObjectLiteral(firstObject) || !isObjectLiteral(secondObject)) {
		return {};
	}

	const output = { ...firstObject };

	Object.keys(secondObject).forEach((key) => {
		if (isObjectLiteral(secondObject[key]) && key in firstObject) {
			output[key] = deepMergeTwoObjects(
				firstObject[key],
				secondObject[key],
			);
		} else {
			output[key] = secondObject[key];
		}
	});

	return output;
}

/**
 * Simple hash function using character codes.
 * Not secure, use only for distinguishing objects
 * with a string of a dozen characters max.
 *
 * @param {{}} object - any object
 * @return {string}
 */
export function hashObject(object) {
	const chars = JSON.stringify(object).split("");

	const hashNumber = chars.reduce((hash, char) => {
		hash = (hash << 5) - hash + char.charCodeAt(0);
		return hash & hash;
	}, 0);

	return hashNumber.toString();
}
